export default [["en-us", {
  "application_name": "ADIsimDDS",
  "appspinner_defaultContent": "Applying Parameter Values",
  "dds": {
    "clock_input_label": "Clock Input",
    "device_dropdown_entry_text": "{device} – {samplerate} {units} with {width}-bit DAC",
    "header": "DDS Selection and Operation",
    "message_max_inp_clk_multi": "Max Clock Input with {multiplier}x Multiplier = {frequency}{units}",
    "message_max_sys_clk": "Max system clock for the {device} is {frequency}{units}",
    "message_nyquist_freq": "Output frequency cannot exceed\nNyquist Frequency of {frequency}{units}",
    "multiplier_list_label": "Multiplier",
    "multiplier_selection": "Select Multiplier",
    "system_clock_label": "System Clock",
    "target_output_freq_label": "Target Output Frequency",
    "x_symbol_label": "×"
  },
  "debug": {
    "discarded_bits_label": "Discarded Bits (Field ID: 2981):",
    "master_freq_clock_label": "Master Frequency Clock (Field ID: 379):",
    "multiplier_label": "Multiplier (Field ID: 2982):",
    "part_product_data": "Product Data for Part ",
    "ref_clk_min_freq_label": "Reference Clock Minimum Frequency (Field ID: 3013):",
    "ref_clk_min_freq_multi_label": "Reference Clock Minimum Frequency w/ Multiplier (Field ID: 3014):",
    "resolution_bits_label": "Resolution Bits (Field ID: 7):",
    "spur_coeff_1_label": "Spur Coefficient 1 (Field ID: 2975):",
    "spur_coeff_2_label": "Spur Coefficient 2 (Field ID: 2976):",
    "spur_coeff_3_label": "Spur Coefficient 3 (Field ID: 2977):",
    "spur_coeff_4_label": "Spur Coefficient 4 (Field ID: 2978):",
    "spur_coeff_5_label": "Spur Coefficient 5 (Field ID: 2979):",
    "spur_coeff_6_label": "Spur Coefficient 6 (Field ID: 2980):",
    "tuning_word_width_label": "Tuning Word Width (Field ID: 1225):"
  },
  "decibel_unit": "dB",
  "dialog": {
    "block_diagram": {
      "content": "<div class=\"help-dialog--special\"><p>The block diagram shown is product agnostic, showing only the fundamental elements of a DDS. Refer to the product data sheet for a more complete and more product specific diagrams.</p><img id=\"block_diagram_dialog_image\" class=\"block-diagram-image\" data-qa-element=\"simdds_block_diagram_dialog_img\" alt=\"Block Diagram of DDS with filter\" src=\"{content}/images/DDSBlockDiagram.jpg\"/></div>",
      "header": "Block Diagram",
      "id": "block-diagram"
    },
    "close_button": "Close",
    "feedback_survey": {
      "cancel": "Cancel",
      "email": "EMail: ",
      "feedback": "Your Feedback",
      "feedbackDialogNgMessage": "Failed to send user feedback. This may be due to a poor connection to the server. Please try again.",
      "feedbackDialogOkMessage": "Your feedback has been sent.",
      "giveFeedback": "Tell us",
      "header": "Give us your feedback!",
      "id": "feedback-survey",
      "invitation": "Please let us know your opinion of this tool, good and bad.",
      "name": "Name: ",
      "optional": "(Optional)"
    },
    "ftw_help": {
      "content": "<div class=\"help-dialog--standard\"><p>The output frequency (fout) of a Direct Digital Synthesis (DDS) device is controlled by the System Clock (fs) and the programmed Frequency Tuning Word (FTW).</p><p>The exact relationship is given by the following equation:</p><div class=\"img-center\"><img class=\"img-text-gray\" alt=\"FTW Equation\" src=\"{content}/assets/images/FTWEquation.png\"/></div><p>Where n is the bit-width of the phase accumulator in the DDS core.</p><p>See the product datasheet for more information on how to calculate and program the FTW.</p><p>See <a id=\"tutorial_mt085_link\" data-qa-element=\"simdds_tutorial_mt085_link\" href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">Tutorial MT-085, Fundamentals of Direct Digital Synthesis</a> and <a id=\"technical_tutorial_link\" data-qa-element=\"simdds_technical_tutorial_link\" href=\"{siteUrl}/media/en/training-seminars/tutorials/450968421DDS_Tutorial_rev12-2-99.pdf{eloqua0}\" target=\"_blank\">A Technical Tutorial on Digital Signal Synthesis</a> for more detailed information on DDS operation and performance.</p></div>",
      "header": "Frequency Tuning Word (FTW) Help",
      "id": "ftw-help"
    },
    "menubar_help": {
      "content": "<div class=\"help-dialog--standard\"><h1 class=\"dialog-title\">What is Direct Digital Synthesis?</h1><p>Direct Digital Synthesis is a method by which a digital representation of a sinewave is generated, and then reconstructed to an analog waveform using a DAC.  A DDS device requires a high frequency reference clock, and the output signal frequency is programmed via the Frequency Tuning Word.</p><p>See <a href=\"https://ez.analog.com/docs/DOC-2369{eloqua0}\" target=\"_blank\">Learning more about Direct Digital Synthesis Technology</a> for more information.</p><p>Other reference materials available at <a href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">MT-085</a> and <a href=\"{siteUrl}/dds{eloqua0}\" target=\"_blank\">Direct Digital Synthesis</a>.</p><h1 class=\"dialog-title\">What is ADIsimDDS?</h1><p>This tool uses mathematical equations to model and illustrate the overall performance of the selected device.  ADIsimDDS calculates the required FTW, given the reference clock frequency and desired output frequency.  This tool also models an estimate of the overall spectral performance, and allows the user to explore the effects of external reconstruction filters.</p><p><b>This tool approximates the overall performance of the selected device, and does not calculate all possible errors.  Therefore, the tool should be used as a design aid only, and is not intended to be used as a replacement for actual hardware testing and evaluation.</b></p></div>",
      "header": "Using the ADIsimDDS tool",
      "id": "menubar-help"
    },
    "selection_operation_help": {
      "content": "<div class=\"help-dialog--standard\"><p>Begin by selecting the device you want to simulate - device characteristics, such as maximum System Clock rate, Frequency Tuning Word width, and DAC Resolution are used to calculate and display estimated performance with given conditions.</p><p>Refer to the device data sheet, as well as <a id=\"tutorial_mt085_link\" data-qa-element=\"simdds_tutorial_mt085_link\" href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">Tutorial MT-085, Fundamentals of Direct Digital Synthesis</a> for more information on Direct Digital Synthesis fundamentals and performance.</p></div>",
      "header": "DDS Selection and Operation Help",
      "id": "selection-operation-help"
    },
    "signal_filtering_help": {
      "content": "<div class=\"help-dialog--standard\"><p>A reconstruction filter is often used at the output of a DDS to remove unwanted images and harmonics from the output spectrum.</p><p>When <em>Use Filter</em> is selected, the response of the filter will display in the <em>Frequency Domain</em> plot, in blue.  You can modify the filter characteristics and observe the response.  All frequency elements in the spectrum will be attenuated by the filter response, and the <em>Frequency Domain</em> and <em>Time Domain</em> plots will both show the resulting filtered signal.</p></div>",
      "header": "Use Filter Help",
      "id": "signal-filtering-help"
    },
    "signals_display_help": {
      "content": "<div class=\"help-dialog--standard\"><p><b>Fundamental</b></p><p>In the frequency domain plot, the primary signal is the fundamental frequency, which is located based on the frequency entered in the Target Output Frequency input field in the DDS Selection and Operation input section.</p><p>The amplitude of the fundamental signal (f<sub>out</sub>) varies with frequency according to the sin(x)/x response as given by:</p><div class=\"img-center\"><img class=\"img-text-gray\" alt=\"Sinc Response Equation\" src=\"{content}/assets/images/SincResponse.png\"/></div><p>Where f<sub>out</sub> is the output frequency, and fs is the system clock frequency. Note that for f<sub>out</sub> = 0 (DC), A(f<sub>out</sub>) = 1 (0 dBc) denoting the full scale amplitude of the DAC. A(f<sub>out</sub>) decreases with frequency and for f<sub>out</sub> = fs/2, A(f<sub>out</sub>) = -3.92dBc.</p><p>If the Target Output Frequency is very small, compared to the System Clock, it may help to zoom in to the far left portion of the plot, as it may be difficult to see the fundamental singnal when the plot is zoomed out.</p><p><b>DAC Images</b></p><p>The DAC Images are colored white in the Frequency Domain plot and are a consequence of the conversion from the digital representation of the sine wave to the analog signal.  The DAC images appear as spurs at frequency offsets equidistant to each side of integer multiples of fs (that is, fs, 2fs, 3fs, etc.) with the frequency offset from each integer multiple of fs being fout. The amplitude of the images behave according to the sin(x)/x response as described above for the fundamental.</p><p>See <a href=\"{siteUrl}/mt-002{eloqua0}\" target=\"_blank\">MT-002 - What the Nyquist Criterion Means to Your Sampled Data System Design</a> for more information.</p><p><b>PPT Spur</b></p><p>The PPT spurs (shown in yellow) are caused by a truncation of bits from the accumulator output to the sine conversion input.</p><p>Detailed analysis of PPT spur calculation can be found in <a href=\"{siteUrl}/media/en/technical-documentation/application-notes/AN-1396.pdf{eloqua0}\" target=\"_blank\">AN-1396 - How to Predict the Frequency and Magnitude of the Primary Phase Truncation Spur in the Output Spectrum of a Direct Digital Synthesizer</a>.</p><p><b>Harmonics</b></p><p>The frequency locations 2nd and 3rd harmonics of the DAC (shown in green and red) are calculated from the Frequency Tuning Word (FTW).  The amplitude of these frequency components can vary, depending on device, board layout, etc.  ADIsimDDS allows the user to enter a value for the amplitude of the 2nd and 3rd harmonics.</p><p>While the frequency values are deterministic, and readily calculated by the tool, the amplitude values are for illustrative purposes only.  Your performance is likely to vary.</p></div>",
      "header": "Signals to Display Help",
      "id": "signals-display-help"
    },
    "system": {
      "applicationBusy": "Working...",
      "title": "Application Message"
    }
  },
  "display-options": {
    "2nd_harmonics_label": "2nd Harmonics",
    "3rd_harmonics_label": "3rd Harmonics",
    "dac_images_label": "DAC Images",
    "fundamental_label": "Fundamental",
    "harmonic_db_label": "Harmonic Magnitude",
    "harmonic_db_units": "dBc",
    "harmonics_label": "Harmonics",
    "ppt_db_label": "PPT Spur dB:",
    "ppt_spur_label": "PPT Spur"
  },
  "ember-aditools-page": {
    "card": {
      "checkbox-tooltip": "Toggle",
      "help-tooltip": "Click for help",
      "part-number-tooltip": "Click to select part"
    },
    "common": {
      "cancel": "Cancel",
      "close": "Close",
      "load": "Load",
      "ok": "OK",
      "open": "Open",
      "save": "Save",
      "select": "Select"
    },
    "dialogs": {
      "feedback": {
        "email": "EMail: ",
        "feedback": "Your Feedback",
        "give-feedback": "Tell us",
        "invitation": "Please let us know your opinion of this tool, good and bad.",
        "name": "Name: ",
        "optional": "(Optional)",
        "title": "Give us your feedback!"
      },
      "load": {
        "choose-button": "Choose File",
        "message": "Load a design from a file on your computer.",
        "no-file-selected": "No file selected.",
        "title": "Load Design"
      },
      "samples": {
        "add-button": "Add Selected Samples to Cart",
        "add-link": "Add to Order",
        "add-to-cart-column": "Add Sample to Cart",
        "model-column": "Device Model/Package",
        "part-column": "Device Name",
        "quantities-warning": "Analog Devices offers a limited amount of free samples to qualified customers. Due to the large demand for our product samples we ask that you limit your sample request to 2 pieces per part with a maximum of 4 parts per request within a 14 day time period. Should your design needs require additional product quantities, you can purchase them on the web through Buy Online, or you can contact one of our authorized distributors.",
        "title": "Order Samples",
        "unavailable-message": "Not available for sample"
      },
      "save": {
        "save-desc-error": "Please provide a Description",
        "save-desc-field": "Description",
        "save-name-error": "Please provide a Design Name",
        "save-name-field": "Design Name",
        "title": "Save Design"
      },
      "share": {
        "share-button": "Email",
        "share-design-information": "<p>To share a copy of your design, enter your name into the <b>Sender name</b> field. Then enter the email address of the recipients in the <b>Share design with</b> field.</p><p>The recipients will receive an email from Analog Devices with an attachment containing a ZIP archive of the shared design. All you have to do is extract the design from the ZIP file and upload it using the Load Design functionality of the application</p><p>This process creates a copy of the design for each recipient. If recipients choose to make edits on their copies, it will have no effect on your design you saved to your own computer.</p>",
        "share-dialog-ng-message": "Unable to share design. This is likely due to a server connection error. Please try again.",
        "share-dialog-ok-message": "Design has been shared",
        "share-duplicate-email": "Duplicate emails found",
        "share-email-empty-error": "Please enter an email address",
        "share-email-field": "Share design with",
        "share-email-format-error": "At least one of the email addresses you entered is invalid. Please correct and try again",
        "share-email-placeholder": "Email Address",
        "share-name-error": "Please provide a Design Name",
        "share-name-field": "Design Name",
        "share-sender-error": "Please provide a Sender Name",
        "share-sender-field": "Sender Name",
        "title": "Share My Design"
      },
      "system": {
        "application-busy": "Working...",
        "title": "Application Message"
      }
    },
    "footer": {
      "accessibility": "Accessibility",
      "cookie-settings": "Cookie Settings",
      "copyright-text": "1995 - 2025 Analog Devices, Inc. All Rights Reserved",
      "icp-id": "",
      "legal": "Legal and Risk",
      "privacy-policy": "Privacy Policy",
      "privacy-settings": "Privacy Settings",
      "sitemap": "Sitemap"
    },
    "header": {
      "applications": "Applications",
      "company": "Company",
      "design-center": "Design Center",
      "education": "Education",
      "parametric-search": "Parametric Search",
      "products": "Products",
      "spoken": {
        "homepage": "ADI Home Page",
        "sitelinks": "ADI Site Links"
      },
      "support": "Support"
    },
    "menubar": {
      "chinese": "简体中文",
      "english": "English",
      "help": "Help",
      "japanese": "日本語",
      "load": "Load Design",
      "save": "Save"
    },
    "studio": {
      "adcdriver": "ADC Driver",
      "dacebc": "DAC",
      "diamond": "In Amp",
      "filterwizard": "Analog Filter",
      "noise": "Noise",
      "photodiode": "Photodiode",
      "precisionstudio": "ADI<span class=''service-mark''>℠</span> Precision Studio",
      "signalchaindesigner": "Signal Chain",
      "simdds": "DDS",
      "spoken": {
        "studiolinks": "ADI Precision Studio Links"
      },
      "virteval": "Virtual Eval"
    },
    "toolbar": {
      "actions": "Actions"
    }
  },
  "errors": {
    "error_ftw_exceed": "{actual}-bit FTW was entered, but the FTW for {device} cannot exceed {maximum}-bits. Entered FTW is {ftwinput} (HEX)",
    "error_info_header": "Errors and Warnings",
    "error_info_link": "Learn more about this error >",
    "header": "Errors and Warnings",
    "no_errors": "No Errors",
    "warning_attenuated_fundamental_long": "Temporary - more info about this error",
    "warning_attenuated_fundamental_short": "<p>Information: The output signal is being attenuated by the filter.</p>",
    "warning_nyquist_long": "Temporary - more info about this error",
    "warning_nyquist_short": "<p>Warning: The Target Output Frequency is approaching the Nyquist Frequency.  It is recommended that you reduce the Target Output Frequency, or use a faster System Clock.</p>",
    "warning_zooming_required_long": "Temporary - more info about this error",
    "warning_zooming_required_short": "<p>Warning: There are images and/or harmonics at frequency locations that are very close to the fundamental signal.  These spurs may be difficult to see on plot (zooming may be required), but they will adversely affect output signal.</p>"
  },
  "filter": {
    "fc_label": "Fc",
    "order_label": "Order",
    "passband_ripple_label": "Passband Ripple",
    "stopband_attenuation_label": "Stop Band Atten.",
    "topology_bessel": "Bessel",
    "topology_butterworth": "Butterworth",
    "topology_chebyshev": "Chebyshev",
    "topology_inverse_chebyshev": "Inverse Chebyshev",
    "topology_label": "Topology",
    "type_label": "Type",
    "type_lowpass": "Lowpass",
    "use_filter": "Use External Filter"
  },
  "ftw": {
    "actual_out_freq_label": "Actual Output Frequency:",
    "change_button": "Change",
    "header": "Frequency Tuning Word (FTW)",
    "least_significant_bit": "LSB",
    "most_significant_bit": "MSB"
  },
  "harmonics": {
    "amplitude_db_label": "Amplitude\n(dBFS)",
    "fundamental_images_label": "Fundamental Images",
    "none": "NONE",
    "output_frequency_label": "Frequency",
    "ppt_spur_label": "PPT Spur [{index}]:",
    "ppt_spur_value": "{amplitude}dB @ {frequency}",
    "second_harmonic_images_label": "2nd Harmonic Images",
    "third_harmonic_images_label": "3rd Harmonic Images"
  },
  "header": {
    "application_title": "ADIsimDDS (Direct Digital Synthesis)",
    "applications": "Applications",
    "applications_link": "{url}/{adilocale}/applications/",
    "design_center": "Design Center",
    "design_center_link": "{url}/{adilocale}/design-center/",
    "education": "Education",
    "education_link": "{url}/{adilocale}/education/",
    "logo_alt": "Logo",
    "logo_title": "ADI Logo",
    "parametric_search": "Parametric Search",
    "parametric_search_link": "{url}/{adilocale}/parametric-search/",
    "products": "Products",
    "products_link": "{url}/{adilocale}/products/",
    "site_link": "{url}/{adilocale}/",
    "support": "Support",
    "support_link": "{url}/{adilocale}/support/"
  },
  "hertz_unit": "Hz",
  "images_label": "Images",
  "main-panel": {
    "block_diagram_header": "Block Diagram",
    "click_to_view_label": "Click to view",
    "signal_filtering_options_header": "External Filter",
    "signals_display_header": "Signals to Display"
  },
  "menubar": {
    "data_sheets": "Data Sheets",
    "debug": "Debug",
    "feedback": "Feedback",
    "help": "Help",
    "output_signal": "Output Signal",
    "product_page": "Product Page",
    "share": "Share",
    "spur_table": "Spur Table"
  },
  "menubar_feedback": "menubar-feedback",
  "menubar_share": "menubar-share",
  "output-signal": {
    "spur_graph_label": "Frequency Domain",
    "waveform_graph_label": "Time Domain"
  },
  "share": {
    "copy_confirmation": "Copied",
    "copy_to_clipboard": "Copy to Clipboard",
    "dds_forum_link": "https://ez.analog.com/community/dds",
    "dds_link": "Direct Digital Synthesis Support Community",
    "header": "Sharing This SimDDS Configuration Is Easy!",
    "instruction_copy": "1. Copy this link to your clipboard",
    "instruction_paste": "2. Paste it into an email, EngineerZone® forum or a social media post",
    "simdds_support_community": "Direct Digital Synthesis support community",
    "tips": "Want Direct Digital Synthesis tips? Check out EngineerZone®"
  },
  "spur-graph": {
    "amplitude_axis_text": "Amplitude (dBFS)",
    "frequency_axis_text": "Frequency (Hz)"
  },
  "waveform-graph": {
    "amplitude_axis_text": "Amplitude (normalized to full-scale)",
    "hover_text": "<div>Frequency:&nbsp;%1</div><div>Amplitude:&nbsp;%2</div>",
    "time_axis_text": "Time (s)"
  },
  "zoom_reset": "Back to 100%"
}], ["ja-jp", {
  "application_name": "ADIsimDDS",
  "appspinner_defaultContent": "Applying Parameter Values",
  "dds": {
    "clock_input_label": "Clock Input",
    "device_dropdown_entry_text": "{device} – {samplerate} {units} with {width}-bit DAC",
    "header": "DDS の選択と動作",
    "message_max_inp_clk_multi": "Max Clock Input with {multiplier}x Multiplier = {frequency}{units}",
    "message_max_sys_clk": "Max system clock for the {device} is {frequency}{units}",
    "message_nyquist_freq": "Output frequency cannot exceed\nNyquist Frequency of {frequency}{units}",
    "multiplier_list_label": "Multiplier",
    "multiplier_selection": "Select Multiplier",
    "system_clock_label": "System Clock",
    "target_output_freq_label": "Target Output Frequency",
    "x_symbol_label": "×"
  },
  "debug": {
    "discarded_bits_label": "Discarded Bits (Field ID: 2981):",
    "master_freq_clock_label": "Master Frequency Clock (Field ID: 379):",
    "multiplier_label": "Multiplier (Field ID: 2982):",
    "part_product_data": "Product Data for Part ",
    "ref_clk_min_freq_label": "Reference Clock Minimum Frequency (Field ID: 3013):",
    "ref_clk_min_freq_multi_label": "Reference Clock Minimum Frequency w/ Multiplier (Field ID: 3014):",
    "resolution_bits_label": "Resolution Bits (Field ID: 7):",
    "spur_coeff_1_label": "Spur Coefficient 1 (Field ID: 2975):",
    "spur_coeff_2_label": "Spur Coefficient 2 (Field ID: 2976):",
    "spur_coeff_3_label": "Spur Coefficient 3 (Field ID: 2977):",
    "spur_coeff_4_label": "Spur Coefficient 4 (Field ID: 2978):",
    "spur_coeff_5_label": "Spur Coefficient 5 (Field ID: 2979):",
    "spur_coeff_6_label": "Spur Coefficient 6 (Field ID: 2980):",
    "tuning_word_width_label": "Tuning Word Width (Field ID: 1225):"
  },
  "decibel_unit": "dB",
  "dialog": {
    "block_diagram": {
      "content": "<div class=\"help-dialog--special\"><p>ここに示すブロック図は特定の製品を表すものではなく、DDS の基本要素だけを示したものです。各製品の具体的かつ詳細なブロック図については、製品のデータシートを参照してください。</p><img id=\"block_diagram_dialog_image\" class=\"block-diagram-image\" data-qa-element=\"simdds_block_diagram_dialog_img\" alt=\"Block Diagram of DDS with filter\" src=\"{content}/images/DDSBlockDiagram.jpg\"/></div>",
      "header": "ブロック図",
      "id": "block-diagram"
    },
    "close_button": "Close",
    "feedback_survey": {
      "cancel": "キャンセル",
      "email": "お客様のメールアドレス：",
      "feedback": "ご意見、ご要望：",
      "feedbackDialogNgMessage": "フィードバックの送信に失敗しました。ネットワーク接続に問題がある場合があります。お手数ですが再度お試しください。",
      "feedbackDialogOkMessage": "ご意見、ご要望をご記入頂きありがとうございました。",
      "giveFeedback": "意見または感想を送る",
      "header": "皆様のご意見をお聞かせください！",
      "id": "feedback-survey",
      "invitation": "このツールに関する率直なご意見、ご要望をお聞かせください。 ",
      "name": "お客様のお名前：",
      "optional": "(Optional)"
    },
    "ftw_help": {
      "content": "<div class=\"help-dialog--standard\"><p>ダイレクト・デジタル合成（DDS）デバイスの出力周波数（fout）は、システム・クロック（fs）とプログラムされた周波数チューニング・ワード（FTW）によって制御します。</p><p>正確な関係は次式で表されます：</p><div class=\"img-center\"><img class=\"img-text-gray\" alt=\"FTW Equation\" src=\"{content}/assets/images/FTWEquation.png\"/></div><p>ここで、n は DDS コア内の位相アキュムレータのビット幅です。</p><p>FTW の計算およびプログラム方法の詳細については、製品のデータシートを参照してください。</p><p>DDS の動作と性能の詳細については、 <a id=\"tutorial_mt085_link\" data-qa-element=\"simdds_tutorial_mt085_link\" href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">Tutorial MT-085, Fundamentals of Direct Digital Synthesis</a>（英語） と <a id=\"technical_tutorial_link\" data-qa-element=\"simdds_technical_tutorial_link\" href=\"{siteUrl}/media/en/training-seminars/tutorials/450968421DDS_Tutorial_rev12-2-99.pdf{eloqua0}\" target=\"_blank\">A Technical Tutorial on Digital Signal Synthesis</a> （英語）を参照してください。</p></div>",
      "header": "周波数チューニング・ワード（FTW）に関するヘルプ",
      "id": "ftw-help"
    },
    "menubar_help": {
      "content": "<div class=\"help-dialog--standard\"><h1 class=\"dialog-title\">ダイレクト・デジタル合成（DDS)とは？</h1><p>ダイレクト・デジタル合成（DDS)は、正弦波をデジタル化して生成する方法で、DAC を使ってアナログ波形に再構成します。DDS デバイスには高周波リファレンス・クロックが必要で、出力信号周波数は周波数チューニング・ワードによってプログラムします。</p><p>詳細については <a href=\"https://ez.analog.com/docs/DOC-2369{eloqua0}\" target=\"_blank\">「ダイレクト・デジタル合成の詳細（Learning more about Direct Digital Synthesis Technology）」（英語）</a>を参照してください。</p><p>その他、 <a href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">MT-085</a> （英語）と <a href=\"{siteUrl}/jp/products/clock-and-timing/direct-digital-synthesis.html{eloqua0}\" target=\"_blank\">Direct Digital Synthesis</a> にも参考資料があります。</p><h1 class=\"dialog-title\"> ADIsimDD評価ツールとは？</h1><p>このツールは、数式を使用して選択したデバイスの全体的性能をモデル化し、その内容を表示します。ADIsimDD評価ツールは、指定されたリファレンス・クロック周波数と必要な出力周波数に基づいて、必要な FTW を計算します。また、このツールで全体的なスペクトル性能の予測をモデル化して、外付け再生フィルタの効果を検討することができます。</p><p><b>このツールは選択したデバイスの全体的性能を近似しますが、考えられるすべての誤差を計算することはできません。このツールは設計支援用としてご使用いただくものであり、実際のハードウェアのテストや評価の代わりになるものではありません。</b></p></div>",
      "header": " ADIsimDD評価ツールの使用について",
      "id": "menubar-help"
    },
    "selection_operation_help": {
      "content": "<div class=\"help-dialog--standard\"><p>最初に、シミュレートしたいデバイスを選択します。最大システム・クロック・レート、周波数チューニング・ワード幅、DAC 分解能などのデバイス特性を使用して、与えられた条件の下に予想性能を計算し、その結果を表示します。</p><p>ダイレクト・デジタル合成の基礎と性能の詳細については、 デバイスのデータシート、および<a id=\"tutorial_mt085_link\" data-qa-element=\"simdds_tutorial_mt085_link\" href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">チュートリアル MT-085、「ダイレクト・デジタル合成の基礎（Fundamentals of Direct Digital Synthesis）」（英語）</a>を参照してください。</p></div>",
      "header": "DDS の選択と動作のヘルプ",
      "id": "selection-operation-help"
    },
    "signal_filtering_help": {
      "content": "<div class=\"help-dialog--standard\"><p>多くの場合、DDS の出力には、出力スペクトラムから不要なイメージや高調波を除去するために再生フィルタが使われます。</p><p><em>「フィルタを使用」</em>を選択すると、<em>周波数領域</em>プロットに青でフィルタの応答が表示されます。フィルタ特性に変更を加えて、その応答を確認することができます。スペクトラム内のすべての周波数要素がフィルタ応答によって減衰され、フィルタ処理後の最終的な信号が<em>周波数領域</em>プロットと<em>時間領域</em>プロットの両方に表示されます。</p></div>",
      "header": "フィルタを使用に関するヘルプ",
      "id": "signal-filtering-help"
    },
    "signals_display_help": {
      "content": "<div class=\"help-dialog--standard\"><p><b>原理</b></p><p>周波数領域プロットの主信号は基本周波数で、その位置は、「DDS の選択と動作」入力セクションにある「ターゲット出力周波数」入力フィールドに入力された周波数に基づいて決定されます。</p><p>基本信号 (f<sub>out</sub>) の振幅は、次式で与えられる sin(x)/x の応答に従い、周波数によって変化します：</p><div class=\"img-center\"><img class=\"img-text-gray\" alt=\"Sinc Response Equation\" src=\"{content}/assets/images/SincResponse.png\"/></div><p>ここで f<sub>out</sub> は出力周波数、fs はシステム・クロック周波数です。f<sub>out</sub> = 0 (DC) の場合は A(f<sub>out</sub>) = 1 (0 dBc) で、これは DAC のフルスケール振幅になります。A(f<sub>out</sub>) は周波数とともに低下し、f<sub>out</sub> = fs/2 の時の値は A(f<sub>out</sub>) = -3.92dBc です。</p><p>ターゲット出力周波数がシステム・クロックと比べて非常に小さい場合は、縮小したプロットでは基本信号がわかりにくいので、プロットの左側の部分を拡大すると見やすくなります。</p><p><b>DAC イメージ</b></p><p>周波数領域プロットでは DAC イメージが白で表示されます。これは、正弦波のデジタル表現をアナログ信号に変換した結果です。DAC イメージは、fs の整数倍（fs、2fs、3fs など）の両側等距離の位置にある周波数オフセットにスプリアスとして現れます。fs の整数倍からの周波数オフセットが fout です。イメージの振幅は、基本波について上に示したように、sin(x)/x の応答に従って動作します。</p><p>詳細については、<a href=\"{siteUrl}/media/jp/training-seminars/tutorials/MT-002_jp.pdf{eloqua0}\" target=\"_blank\">MT-002 - 「ナイキストの基準を現実のADC システムの設計に活かす」</a> を参照してください。</p><p><b>PPT スプリアス</b></p><p>PPT スプリアス（黄色で表示）は、アキュムレータ出力から正弦波入力へのビットの切り捨てによって生じます。</p><p>PPT スプリアス計算の詳細な解析については、 <a href=\"{siteUrl}/media/jp/technical-documentation/application-notes/AN-1396_jp.pdf{eloqua0}\" target=\"_blank\">AN-1396 -ダイレクト・デジタル・シンセサイザ（DDS）の出力ペクトルにおける1 次位相切り捨てスプリアスの周波数および振幅予測方法</a> をご覧ください。</p><p><b>高調波</b></p><p>DAC の第 2 および第 3 高調波の周波数位置（緑と赤で表示）は、周波数チューニング・ワード（FTW）から計算します。これらの周波数成分の振幅は、デバイスやボード・レイアウトなどに応じて変化します。ADIsimDDS では、第 2 および 第 3 高調波の振幅の値をユーザが入力できます。</p><p>周波数の値は確定的なもので、ツールで計算できますが、振幅値は説明のために使用するものにすぎません。実際の性能とは異なることがあります。</p></div>",
      "header": "表示する信号に関するヘルプ",
      "id": "signals-display-help"
    },
    "system": {
      "applicationBusy": "処理中・・・",
      "title": "アプリケーション・メッセージ"
    }
  },
  "display-options": {
    "2nd_harmonics_label": "2nd Harmonics",
    "3rd_harmonics_label": "3rd Harmonics",
    "dac_images_label": "DAC Images",
    "fundamental_label": "Fundamental",
    "harmonic_db_label": "Harmonic Magnitude",
    "harmonic_db_units": "dBc",
    "harmonics_label": "Harmonics",
    "ppt_db_label": "PPT Spur dB:",
    "ppt_spur_label": "PPT Spur"
  },
  "ember-aditools-page": {
    "card": {
      "checkbox-tooltip": "Toggle",
      "help-tooltip": "Click for help",
      "part-number-tooltip": "Click to select part"
    },
    "common": {
      "cancel": "キャンセル",
      "close": "閉じる",
      "load": "読み込む",
      "ok": "OK",
      "open": "開く",
      "save": "保存する",
      "select": "選ぶ"
    },
    "dialogs": {
      "feedback": {
        "email": "お客様のメールアドレス：",
        "feedback": "ご意見、ご要望：",
        "give-feedback": "意見または感想を送る",
        "invitation": "このツールに関する率直なご意見、ご要望をお聞かせください。 ",
        "name": "お客様のお名前：",
        "optional": "(Optional)",
        "title": "皆様のご意見をお聞かせください！"
      },
      "load": {
        "choose-button": "ファイルを選択",
        "message": "ローカルPC上の設計ファイルを読み込む",
        "no-file-selected": "No file selected.",
        "title": "設計を読み込む"
      },
      "samples": {
        "add-button": "選択したサンプルをカートに入れる",
        "add-link": "Add to Order",
        "add-to-cart-column": "サンプルをカートに入れる",
        "model-column": "モデル／パッケージ",
        "part-column": "構成部品",
        "quantities-warning": "アナログ・デバイセズは認証させていただいたお客様に限り、無料サンプルのご注文を受け付けています。 サンプルについては大変多くのご要望をいただいておりますため、おひとり様2週間あたり最大で4製品、各2個ずつまでのご注文とさせていただきます。無料サンプルとしてご注文いただける以上の量が必要な場合は、オンライン購入をしていただくか（在庫がある場合に限ります）、販売代理店にお問い合わせください。",
        "title": "サンプル注文",
        "unavailable-message": "サンプルの提供はありません。"
      },
      "save": {
        "save-desc-error": "概要を入力してください。",
        "save-desc-field": "概要",
        "save-name-error": "デザイン名を入力してください。",
        "save-name-field": "デザイン名",
        "title": "保存"
      },
      "share": {
        "share-button": "Email",
        "share-design-information": "<p>設計ファイルのコピーを共有するには、<b>“差出人名”</b>にお客様のお名前を、共有したい方のメールアドレスを<b>“設計を共有”</b>項目にご入力ください。送信先が複数の場合には、それぞれのメールアドレスを改行してご入力ください。</p><p>アナログ･デバイセズよりZIPファイルにまとめられた設計ファイルが添付されたメールを、ご入力いただいたメールアドレスにお送りします。添付されたZIPファイルを解凍して、各ツールのアップロード機能を使用して設計ファイルをアップロードするだけで、その設計ファイルを使用することが出来ます。</p><p>上記手順により、共有された設計ファイルのコピーを使用することが出来ます。ただし、共有された設計ファイルのコピーが編集されても、お客様のオリジナルの設計ファイルが編集されることはありません。</p>",
        "share-dialog-ng-message": "回路を共有できません。サーバーの接続不良による可能性があります。再度お試しください。",
        "share-dialog-ok-message": "回路は共有されました",
        "share-duplicate-email": "同じメールアドレスが複数含まれています",
        "share-email-empty-error": "メールアドレスを入力してください。",
        "share-email-field": "設計を共有",
        "share-email-format-error": "お客様が入力したメールアドレスの中で少なくとも１つは無効です。修正して再度お試しください。",
        "share-email-placeholder": "メールアドレス",
        "share-name-error": "デザイン名を入力してください。",
        "share-name-field": "デザイン名",
        "share-sender-error": "差出人名を入力してください。",
        "share-sender-field": "差出人名",
        "title": "自分の設計をメールで送信"
      },
      "system": {
        "application-busy": "処理中・・・",
        "title": "アプリケーション・メッセージ"
      }
    },
    "footer": {
      "accessibility": "アクセシビリティ",
      "cookie-settings": "Cookieの設定",
      "copyright-text": "1995 - 2025 Analog Devices, Inc. 全著作権所有",
      "icp-id": "",
      "legal": "法的事項およびリスク約",
      "privacy-policy": "プライバシーポリシー",
      "privacy-settings": "プライバシー設定",
      "sitemap": "サイトマップ"
    },
    "header": {
      "applications": "アプリケーション",
      "company": "企業情報",
      "design-center": "設計支援",
      "education": "調べる／学ぶ",
      "parametric-search": "パラメータ検索",
      "products": "製品",
      "spoken": {
        "homepage": "ホームページ",
        "sitelinks": "アナログ・デバイセズのサイトへのリンク"
      },
      "support": "サポート"
    },
    "menubar": {
      "chinese": "简体中文",
      "english": "English",
      "help": "ヘルプ",
      "japanese": "日本語",
      "load": "設計を読み込む",
      "save": "保存"
    },
    "studio": {
      "adcdriver": "ADCドライバ",
      "dacebc": "DAC",
      "diamond": "計装アンプ",
      "filterwizard": "アナログ・フィルタ",
      "noise": "ノイズ",
      "photodiode": "フォトダイオード",
      "precisionstudio": "ADI<span class=''service-mark''>℠</span> Precision Studio",
      "signalchaindesigner": "Signal Chain",
      "simdds": "DDS",
      "spoken": {
        "studiolinks": "ADI Precision Studio へのリンク"
      },
      "virteval": "Virtual Eval"
    },
    "toolbar": {
      "actions": "Actions"
    }
  },
  "errors": {
    "error_ftw_exceed": "{actual}-bit FTW was entered, but the FTW for {device} cannot exceed {maximum}-bits. Entered FTW is {ftwinput} (HEX)",
    "error_info_header": "Errors and Warnings",
    "error_info_link": "Learn more about this error >",
    "header": "Errors and Warnings",
    "no_errors": "No Errors",
    "warning_attenuated_fundamental_long": "Temporary - more info about this error",
    "warning_attenuated_fundamental_short": "<p>Information: The output signal is being attenuated by the filter.</p>",
    "warning_nyquist_long": "Temporary - more info about this error",
    "warning_nyquist_short": "<p>Warning: The Target Output Frequency is approaching the Nyquist Frequency.  It is recommended that you reduce the Target Output Frequency, or use a faster System Clock.</p>",
    "warning_zooming_required_long": "Temporary - more info about this error",
    "warning_zooming_required_short": "<p>Warning: There are images and/or harmonics at frequency locations that are very close to the fundamental signal.  These spurs may be difficult to see on plot (zooming may be required), but they will adversely affect output signal.</p>"
  },
  "filter": {
    "fc_label": "Fc",
    "order_label": "Order",
    "passband_ripple_label": "Passband Ripple",
    "stopband_attenuation_label": "Stop Band Atten.",
    "topology_bessel": "Bessel",
    "topology_butterworth": "Butterworth",
    "topology_chebyshev": "Chebyshev",
    "topology_inverse_chebyshev": "Inverse Chebyshev",
    "topology_label": "Topology",
    "type_label": "Type",
    "type_lowpass": "Lowpass",
    "use_filter": "外付けフィルタを使用"
  },
  "ftw": {
    "actual_out_freq_label": "Actual Output Frequency:",
    "change_button": "Change",
    "header": "周波数チューニング・ワード（FTW）",
    "least_significant_bit": "LSB",
    "most_significant_bit": "MSB"
  },
  "harmonics": {
    "amplitude_db_label": "Amplitude\n(dBFS)",
    "fundamental_images_label": "Fundamental Images",
    "none": "NONE",
    "output_frequency_label": "Frequency",
    "ppt_spur_label": "PPT Spur [{index}]:",
    "ppt_spur_value": "{amplitude}dB @ {frequency}",
    "second_harmonic_images_label": "2nd Harmonic Images",
    "third_harmonic_images_label": "3rd Harmonic Images"
  },
  "header": {
    "application_title": "ADIsimDDS (Direct Digital Synthesis)評価ツール",
    "applications": "アプリケーション",
    "applications_link": "{url}/{adilocale}/applications/",
    "design_center": "設計支援",
    "design_center_link": "{url}/{adilocale}/design-center/",
    "education": "調べる／学ぶ",
    "education_link": "{url}/{adilocale}/education/",
    "logo_alt": "Logo",
    "logo_title": "ADI Logo",
    "parametric_search": "パラメータ検索",
    "parametric_search_link": "{url}/{adilocale}/parametric-search/",
    "products": "製品",
    "products_link": "{url}/{adilocale}/products/",
    "site_link": "{url}/{adilocale}/",
    "support": "サポート",
    "support_link": "{url}/{adilocale}/support/"
  },
  "hertz_unit": "Hz",
  "images_label": "Images",
  "main-panel": {
    "block_diagram_header": "ブロック図",
    "click_to_view_label": "Click to view",
    "signal_filtering_options_header": "外付けフィルタ",
    "signals_display_header": "表示する信号"
  },
  "menubar": {
    "data_sheets": "データシート",
    "debug": "Debug",
    "feedback": "フィードバック",
    "help": "ヘルプ",
    "output_signal": "出力信号",
    "product_page": "製品ページ",
    "share": "共有",
    "spur_table": "スプリアス"
  },
  "menubar_feedback": "menubar-feedback",
  "menubar_share": "menubar-share",
  "output-signal": {
    "spur_graph_label": "Frequency Domain",
    "waveform_graph_label": "Time Domain"
  },
  "share": {
    "copy_confirmation": "Copied",
    "copy_to_clipboard": "クリップボードにコピー",
    "dds_forum_link": "https://ez.analog.com/community/dds",
    "dds_link": "ADIsimDDSサポート・コミュニティ",
    "header": "このADIsimDDSの構成は簡単に共有できます。",
    "instruction_copy": "1.このリンクをクリップボードにコピーします。",
    "instruction_paste": "2.メール、EngineerZone＠フォーラム、SNSの投稿にリンクを貼り付けます。",
    "simdds_support_community": "ADIsimDDSサポート・コミュニティ",
    "tips": "ADIsimDDSに関するヒントが必要な場合は、EngineerZone? をご参照ください。"
  },
  "spur-graph": {
    "amplitude_axis_text": "Amplitude (dBFS)",
    "frequency_axis_text": "Frequency (Hz)"
  },
  "waveform-graph": {
    "amplitude_axis_text": "Amplitude (normalized to full-scale)",
    "hover_text": "<div>Frequency:&nbsp;%1</div><div>Amplitude:&nbsp;%2</div>",
    "time_axis_text": "Time (s)"
  },
  "zoom_reset": "Back to 100%"
}], ["zh-cn", {
  "application_name": "ADIsimDDS",
  "appspinner_defaultContent": "Applying Parameter Values",
  "dds": {
    "clock_input_label": "Clock Input",
    "device_dropdown_entry_text": "{device} – {samplerate} {units} with {width}-bit DAC",
    "header": "DDS选型和操作",
    "message_max_inp_clk_multi": "Max Clock Input with {multiplier}x Multiplier = {frequency}{units}",
    "message_max_sys_clk": "Max system clock for the {device} is {frequency}{units}",
    "message_nyquist_freq": "Output frequency cannot exceed\nNyquist Frequency of {frequency}{units}",
    "multiplier_list_label": "Multiplier",
    "multiplier_selection": "Select Multiplier",
    "system_clock_label": "System Clock",
    "target_output_freq_label": "Target Output Frequency",
    "x_symbol_label": "×"
  },
  "debug": {
    "discarded_bits_label": "Discarded Bits (Field ID: 2981):",
    "master_freq_clock_label": "Master Frequency Clock (Field ID: 379):",
    "multiplier_label": "Multiplier (Field ID: 2982):",
    "part_product_data": "Product Data for Part ",
    "ref_clk_min_freq_label": "Reference Clock Minimum Frequency (Field ID: 3013):",
    "ref_clk_min_freq_multi_label": "Reference Clock Minimum Frequency w/ Multiplier (Field ID: 3014):",
    "resolution_bits_label": "Resolution Bits (Field ID: 7):",
    "spur_coeff_1_label": "Spur Coefficient 1 (Field ID: 2975):",
    "spur_coeff_2_label": "Spur Coefficient 2 (Field ID: 2976):",
    "spur_coeff_3_label": "Spur Coefficient 3 (Field ID: 2977):",
    "spur_coeff_4_label": "Spur Coefficient 4 (Field ID: 2978):",
    "spur_coeff_5_label": "Spur Coefficient 5 (Field ID: 2979):",
    "spur_coeff_6_label": "Spur Coefficient 6 (Field ID: 2980):",
    "tuning_word_width_label": "Tuning Word Width (Field ID: 1225):"
  },
  "decibel_unit": "dB",
  "dialog": {
    "block_diagram": {
      "content": "<div class=\"help-dialog--special\"><p>所示框图与产品无关，仅显示DDS的基本要素。更完整和更多特定产品图请参考产品数据手册。</p><img id=\"block_diagram_dialog_image\" class=\"block-diagram-image\" data-qa-element=\"simdds_block_diagram_dialog_img\" alt=\"Block Diagram of DDS with filter\" src=\"{content}/images/DDSBlockDiagram.jpg\"/></div>",
      "header": "框图",
      "id": "block-diagram"
    },
    "close_button": "Close",
    "feedback_survey": {
      "cancel": "取消",
      "email": "电子邮件：",
      "feedback": "你的反馈：",
      "feedbackDialogNgMessage": "您的反馈发送失败。可能是由于服务器的连接问题，请尝试再次发送。",
      "feedbackDialogOkMessage": "您的反馈已经发送。",
      "giveFeedback": "给予反馈",
      "header": "提交您的意见或反馈！",
      "id": "feedback-survey",
      "invitation": "请告诉我们你对这款工具的看法，任何好的或坏的。 ",
      "name": "姓名：",
      "optional": "(Optional)"
    },
    "ftw_help": {
      "content": "<div class=\"help-dialog--standard\"><p>直接数字频率合成(DDS)器件的输出频率(fout)由系统时钟(fs)和编程频率调谐字(FTW)控制。</p><p>确切的关系可通过以下公式计算：</p><div class=\"img-center\"><img class=\"img-text-gray\" alt=\"FTW Equation\" src=\"{content}/assets/images/FTWEquation.png\"/></div><p>其中n为DDS内核中相位累加器的位宽。</p><p>有关如何计算和编程FTW的更多信息，请参见产品数据手册。</p><p>有关DDS操作和性能的更多详细信息，请参见<a id=\"tutorial_mt085_link\" data-qa-element=\"simdds_tutorial_mt085_link\" href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">指南MT-085：直接数字频率合成(DDS)基本原理</a>和<a id=\"technical_tutorial_link\" data-qa-element=\"simdds_technical_tutorial_link\" href=\"{siteUrl}/media/en/training-seminars/tutorials/450968421DDS_Tutorial_rev12-2-99.pdf{eloqua0}\" target=\"_blank\">数字信号合成技术指南</a>。</p></div>",
      "header": "频率调谐字(FTW)帮助",
      "id": "ftw-help"
    },
    "menubar_help": {
      "content": "<div class=\"help-dialog--standard\"><h1 class=\"dialog-title\">什么是直接数字频率合成？</h1><p>直接数字频率合成(DDS)是一种生成正弦波数字表示并使用DAC将其重构为模拟波形的方法。DDS器件需要高频参考时钟，输出信号频率通过频率调谐字进行配置。</p><p>更多信息，请参见<a href=\"https://ez.analog.com/docs/DOC-2369{eloqua0}\" target=\"_blank\">了解有关直接数字频率合成的更多信息</a>。</p><p>如需其他参考资料，请参见<a href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">MT-085</a> 和 <a href=\"{siteUrl}/zh/products/clock-and-timing/direct-digital-synthesis.html{eloqua0}\" target=\"_blank\">Direct Digital Synthesis</a>.</p><h1 class=\"dialog-title\">什么是ADIsimDDS？</h1><p>该工具利用数学公式模拟和显示选定器件的整体性能。ADIsimDDS根据参考时钟频率和所需的输出频率计算所需的FTW。该工具还对整体频谱性能估算进行模拟，允许用户探讨外部重建滤波器的效应。</p><p><b>该工具可估算选定器件的整体性能，但并不计算所有可能的误差。因此，这款工具只能用来辅助设计，而不能代替实际的硬件测试和评估。</b></p></div>",
      "header": "使用ADIsimDDS工具",
      "id": "menubar-help"
    },
    "selection_operation_help": {
      "content": "<div class=\"help-dialog--standard\"><p>首先选择您想模拟器件特性的器件，如最大系统时钟速率。频率调谐字宽度和DAC分辨率用于计算和显示给定条件下的预估性能。</p><p>有关直接数字频率合成基本原理和性能的更多信息，请参考<a id=\"tutorial_mt085_link\" data-qa-element=\"simdds_tutorial_mt085_link\" href=\"{siteUrl}/mt-085{eloqua0}\" target=\"_blank\">器件数据手册以及指南MT-085</a> for more information on Direct Digital Synthesis fundamentals and performance.</p></div>",
      "header": "DDS选型和操作帮助",
      "id": "selection-operation-help"
    },
    "signal_filtering_help": {
      "content": "<div class=\"help-dialog--standard\"><p>重构滤波器一般用于DAC的输出端消除输出频谱中无用的镜像和谐波。</p><p>选用<i>使用滤波器</i>时，滤波器的响应在<i>频域</i>图中显示为蓝色。可以更改滤波器特性并观察响应。频谱中的所有频率成分都会通过滤波器响应衰减，而<i>频域</i>和<i>时域</i>图会同时显示所得的滤波信号。</p></div>",
      "header": "使用滤波器帮助",
      "id": "signal-filtering-help"
    },
    "signals_display_help": {
      "content": "<div class=\"help-dialog--standard\"><p><b>基频</b></p><p>在频域图中，主信号为基频，其位置基于在\"DDS选型和操作\"输入部分的\"目标输出频率\"输入字段中输入的频率。</p><p>基频信号的幅度(f<sub>out</sub>)根据sin(x)/x响应随频率变化，如下所示：</p><div class=\"img-center\"><img class=\"img-text-gray\" alt=\"Sinc Response Equation\" src=\"{content}/assets/images/SincResponse.png\"/></div><p>其中，f<sub>out</sub>为输出频率，fs为系统时钟频率。注意，如果f<sub>out</sub> = 0 (DC)，A(f<sub>out</sub>) = 1 (0 dBc)表示DAC的满量程幅度。A(f<sub>out</sub>)随频率下降，如果f<sub>out</sub> = fs/2，A(f<sub>out</sub>) = -3.92dBc。</p><p>如果与系统时钟相比目标输出频率非常小，最好在图表的最左侧放大显示，因为当图表缩小时可能很难看到基频信号。</p><p><b>DAC镜像</b></p><p>DAC镜像在频域图中为白色，是从正弦波的数字表示转换为模拟信号的结果。DAC镜像在每侧等距fs（即，fs、2fs、3fs等）的整数倍频率偏移下显示为杂散，各fs整数倍的频率偏移为fout。镜像幅度随上述基频sin(x)/x响应变化。</p><p>更多信息，请参见<a href=\"{siteUrl}/mt-002{eloqua0}\" target=\"_blank\">MT-002 - 奈奎斯特准则对数据采样系统设计有何意义</a> for</p><p><b>PPT杂散</b></p><p>PPT杂散（显示为黄色）由累加器输出端到正弦波转换输入端的位截断引起。</p><p>PPT杂散计算的详细分析参见<a href=\"{siteUrl}/media/en/technical-documentation/application-notes/AN-1396.pdf{eloqua0}\" target=\"_blank\">AN-1396 - 如何预测直接数字频率合成器输出频谱中主相位截断杂散的频率和幅度</a>。</p><p><b>谐波</b></p><p>DAC的频率位置二次和三次谐波（显示为绿色和红色）根据频率调谐字(FTW)计算。这些频率分量的幅度可发生变化，具体取决于器件、电路板布局等。ADIsimDDS允许用户输入二次和三次谐波的幅度值。</p><p>虽然利用工具可以方便地确定和计算频率值，但幅度值仅作为示例提供。性能可能会有所不同。</p></div>",
      "header": "显示信号帮助",
      "id": "signals-display-help"
    },
    "system": {
      "applicationBusy": "处理中...",
      "title": "应用消息"
    }
  },
  "display-options": {
    "2nd_harmonics_label": "2nd Harmonics",
    "3rd_harmonics_label": "3rd Harmonics",
    "dac_images_label": "DAC Images",
    "fundamental_label": "Fundamental",
    "harmonic_db_label": "Harmonic Magnitude",
    "harmonic_db_units": "dBc",
    "harmonics_label": "Harmonics",
    "ppt_db_label": "PPT Spur dB:",
    "ppt_spur_label": "PPT Spur"
  },
  "ember-aditools-page": {
    "card": {
      "checkbox-tooltip": "Toggle",
      "help-tooltip": "Click for help",
      "part-number-tooltip": "Click to select part"
    },
    "common": {
      "cancel": "取消",
      "close": "关闭",
      "load": "加载",
      "ok": "确定",
      "open": "打开",
      "save": "保存",
      "select": "选择"
    },
    "dialogs": {
      "feedback": {
        "email": "电子邮件：",
        "feedback": "你的反馈：",
        "give-feedback": "给予反馈",
        "invitation": "请告诉我们你对这款工具的看法，任何好的或坏的。 ",
        "name": "姓名：",
        "optional": "(Optional)",
        "title": "提交您的意见或反馈！"
      },
      "load": {
        "choose-button": "选择文档",
        "message": "从计算机文件中加载设计",
        "no-file-selected": "No file selected.",
        "title": "加载设计"
      },
      "samples": {
        "add-button": "添加所选样片至购物车",
        "add-link": "Add to Order",
        "add-to-cart-column": "样片加入购物车",
        "model-column": "元器件模型/封装",
        "part-column": "组成部分",
        "quantities-warning": "ADI公司向符合条件的客户免费限量提供样片。 出于客户对我们产品样片有大量需求，两周内，每款器件您只能申请2片，每份申请最多申请4款器件。 如果您的设计需要额外数量的产品，请在网站上通过在线购买或者联系我们的授权代理商购买。",
        "title": "申请样片",
        "unavailable-message": "无法申请样片"
      },
      "save": {
        "save-desc-error": "请提供描述",
        "save-desc-field": "介绍",
        "save-name-error": "请输入设计名称",
        "save-name-field": "设计名称",
        "title": "保存"
      },
      "share": {
        "share-button": "Email",
        "share-design-information": "<p>若要共享您的设计副本，请在 <b>发件人名称</b> 字段中输入您的姓名。然后在 <b>共享设计</b> 字段中输入收件人的电子邮件地址。多个收件人的地址需分行填写。</p><p>收件人将收到ADI发出的电子邮件，内含包括共享设计ZIP存档的附件。您只需要从ZIP文件中提取设计并使用应用的“加载设计”功能上传。</p><p>本过程为每个收件人创建一份设计副本。收件人编辑他们的副本并不会影响您保存在您自己的电脑中的设计。</p>",
        "share-dialog-ng-message": "无法共享设计，这可能是因为服务器连接问题。请重试。",
        "share-dialog-ok-message": "已共享设计",
        "share-duplicate-email": "找到重复的电子邮件",
        "share-email-empty-error": "请输入电子邮件地址",
        "share-email-field": "收件人",
        "share-email-format-error": "您输入的电子邮件地址中有一个或多个无效，请更正后重试。",
        "share-email-placeholder": "电子邮件地址",
        "share-name-error": "请输入设计名称",
        "share-name-field": "设计名称",
        "share-sender-error": "请提交收件人姓名",
        "share-sender-field": "收件人姓名",
        "title": "通过邮件发送我的设计"
      },
      "system": {
        "application-busy": "处理中...",
        "title": "应用消息"
      }
    },
    "footer": {
      "accessibility": "可访问性",
      "cookie-settings": "Cookie设置",
      "copyright-text": "1995 - 2025 Analog Devices, Inc. 保留所有权利",
      "icp-id": "<a href=\"https://beian.miit.gov.cn/\">沪ICP备09046653号-1</a>",
      "legal": "法律与风险",
      "privacy-policy": "隐私政策",
      "privacy-settings": "隐私设置",
      "sitemap": "网站地图"
    },
    "header": {
      "applications": "应用",
      "company": "公司",
      "design-center": "设计中心",
      "education": "教育培训",
      "parametric-search": "参数搜索",
      "products": "产品",
      "spoken": {
        "homepage": "ADI主页",
        "sitelinks": "ADI网站链接"
      },
      "support": "支持"
    },
    "menubar": {
      "chinese": "简体中文",
      "english": "English",
      "help": "帮助",
      "japanese": "日本語",
      "load": "加载设计",
      "save": "保存"
    },
    "studio": {
      "adcdriver": "ADC驱动器",
      "dacebc": "DAC",
      "diamond": "仪表放大器",
      "filterwizard": "模拟滤波器",
      "noise": "噪声",
      "photodiode": "光电二极管",
      "precisionstudio": "ADI<span class=''service-mark''>℠</span> Precision Studio",
      "signalchaindesigner": "Signal Chain",
      "simdds": "DDS",
      "spoken": {
        "studiolinks": "ADI Precision Studio链接"
      },
      "virteval": "Virtual Eval"
    },
    "toolbar": {
      "actions": "Actions"
    }
  },
  "errors": {
    "error_ftw_exceed": "{actual}-bit FTW was entered, but the FTW for {device} cannot exceed {maximum}-bits. Entered FTW is {ftwinput} (HEX)",
    "error_info_header": "故障和警告",
    "error_info_link": "Learn more about this error >",
    "header": "故障和警告",
    "no_errors": "无误差",
    "warning_attenuated_fundamental_long": "Temporary - more info about this error",
    "warning_attenuated_fundamental_short": "<p>Information: The output signal is being attenuated by the filter.</p>",
    "warning_nyquist_long": "Temporary - more info about this error",
    "warning_nyquist_short": "<p>Warning: The Target Output Frequency is approaching the Nyquist Frequency.  It is recommended that you reduce the Target Output Frequency, or use a faster System Clock.</p>",
    "warning_zooming_required_long": "Temporary - more info about this error",
    "warning_zooming_required_short": "<p>Warning: There are images and/or harmonics at frequency locations that are very close to the fundamental signal.  These spurs may be difficult to see on plot (zooming may be required), but they will adversely affect output signal.</p>"
  },
  "filter": {
    "fc_label": "Fc",
    "order_label": "Order",
    "passband_ripple_label": "Passband Ripple",
    "stopband_attenuation_label": "Stop Band Atten.",
    "topology_bessel": "Bessel",
    "topology_butterworth": "Butterworth",
    "topology_chebyshev": "Chebyshev",
    "topology_inverse_chebyshev": "Inverse Chebyshev",
    "topology_label": "Topology",
    "type_label": "Type",
    "type_lowpass": "Lowpass",
    "use_filter": "使用外部滤波器"
  },
  "ftw": {
    "actual_out_freq_label": "Actual Output Frequency:",
    "change_button": "Change",
    "header": "频率调谐字(FTW)",
    "least_significant_bit": "LSB",
    "most_significant_bit": "MSB"
  },
  "harmonics": {
    "amplitude_db_label": "Amplitude\n(dBFS)",
    "fundamental_images_label": "Fundamental Images",
    "none": "NONE",
    "output_frequency_label": "Frequency",
    "ppt_spur_label": "PPT Spur [{index}]:",
    "ppt_spur_value": "{amplitude}dB @ {frequency}",
    "second_harmonic_images_label": "2nd Harmonic Images",
    "third_harmonic_images_label": "3rd Harmonic Images"
  },
  "header": {
    "application_title": "ADIsimDDS (直接数字频率合成)",
    "applications": "应用",
    "applications_link": "{url}/{adilocale}/applications/",
    "design_center": "设计中心",
    "design_center_link": "{url}/{adilocale}/design-center/",
    "education": "教育培训",
    "education_link": "{url}/{adilocale}/education/",
    "logo_alt": "Logo",
    "logo_title": "ADI Logo",
    "parametric_search": "参数搜索",
    "parametric_search_link": "{url}/{adilocale}/parametric-search/",
    "products": "产品",
    "products_link": "{url}/{adilocale}/products/",
    "site_link": "{url}/{adilocale}/",
    "support": "支持",
    "support_link": "{url}/{adilocale}/support/"
  },
  "hertz_unit": "Hz",
  "images_label": "镜像",
  "main-panel": {
    "block_diagram_header": "框图",
    "click_to_view_label": "Click to view",
    "signal_filtering_options_header": " 外部滤波器",
    "signals_display_header": "显示信号"
  },
  "menubar": {
    "data_sheets": "数据手册",
    "debug": "Debug",
    "feedback": "反馈",
    "help": "帮助",
    "output_signal": "输出信号",
    "product_page": "产品页面",
    "share": "共享",
    "spur_table": "杂散表"
  },
  "menubar_feedback": "menubar-feedback",
  "menubar_share": "menubar-share",
  "output-signal": {
    "spur_graph_label": "频域",
    "waveform_graph_label": "时域"
  },
  "share": {
    "copy_confirmation": "Copied",
    "copy_to_clipboard": "复制到剪贴板",
    "dds_forum_link": "https://ez.analog.com/community/dds",
    "dds_link": "直接数字频率合成支持社区",
    "header": "轻松共享SimDDS配置！",
    "instruction_copy": "1. 将此链接复制到剪贴板",
    "instruction_paste": "2. 将其粘贴到电子邮件、EngineerZone®论坛或社交媒体帖子",
    "simdds_support_community": "直接数字频率合成支持社区",
    "tips": "需要关于直接数字频率合成的建议？请访问EngineerZone® "
  },
  "spur-graph": {
    "amplitude_axis_text": "Amplitude (dBFS)",
    "frequency_axis_text": "Frequency (Hz)"
  },
  "waveform-graph": {
    "amplitude_axis_text": "Amplitude (normalized to full-scale)",
    "hover_text": "<div>Frequency:&nbsp;%1</div><div>Amplitude:&nbsp;%2</div>",
    "time_axis_text": "Time (s)"
  },
  "zoom_reset": "Back to 100%"
}]];